/**
 * SKELETON COMPONENT - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 * Based on WWDC25: Meet Liquid Glass
 *
 * Features:
 * - Glass shimmer animation
 * - Multiple preset shapes
 * - Composable for complex layouts
 * - Respects reduced-motion preference
 */

import { cn } from '../../lib/utils'

interface SkeletonProps {
  className?: string
  width?: string | number
  height?: string | number
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full'
  animate?: boolean
}

const roundedClasses = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  full: 'rounded-full',
}

export function Skeleton({
  className,
  width,
  height,
  rounded = 'md',
  animate = true,
}: SkeletonProps) {
  return (
    <div
      className={cn(
        'liquid-skeleton-shimmer',
        roundedClasses[rounded],
        !animate && 'animation-none bg-light-bg-tertiary/50 dark:bg-dark-bg-tertiary/50',
        className
      )}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
      }}
      aria-hidden="true"
    />
  )
}

/**
 * SkeletonText - Text placeholder with multiple lines
 */
interface SkeletonTextProps {
  lines?: number
  lastLineWidth?: string
  gap?: 'sm' | 'md' | 'lg'
  className?: string
}

const gapClasses = {
  sm: 'space-y-2',
  md: 'space-y-3',
  lg: 'space-y-4',
}

export function SkeletonText({
  lines = 3,
  lastLineWidth = '60%',
  gap = 'md',
  className,
}: SkeletonTextProps) {
  return (
    <div className={cn(gapClasses[gap], className)}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          height={16}
          width={index === lines - 1 ? lastLineWidth : '100%'}
          rounded="sm"
        />
      ))}
    </div>
  )
}

/**
 * SkeletonAvatar - Circular avatar placeholder
 */
interface SkeletonAvatarProps {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
}

const avatarSizes = {
  sm: 32,
  md: 40,
  lg: 48,
  xl: 64,
}

export function SkeletonAvatar({
  size = 'md',
  className,
}: SkeletonAvatarProps) {
  const dimension = avatarSizes[size]

  return (
    <Skeleton
      width={dimension}
      height={dimension}
      rounded="full"
      className={className}
    />
  )
}

/**
 * SkeletonCard - Card placeholder with header and content
 */
interface SkeletonCardProps {
  showAvatar?: boolean
  showImage?: boolean
  lines?: number
  className?: string
}

export function SkeletonCard({
  showAvatar = false,
  showImage = false,
  lines = 3,
  className,
}: SkeletonCardProps) {
  return (
    <div
      className={cn(
        'liquid-glass-card p-6 space-y-4',
        className
      )}
    >
      {/* Image placeholder */}
      {showImage && (
        <Skeleton
          height={160}
          rounded="lg"
          className="w-full -mt-6 -mx-6 mb-4"
          style={{ width: 'calc(100% + 48px)', marginTop: '-24px' }}
        />
      )}

      {/* Header with optional avatar */}
      <div className="flex items-center gap-3">
        {showAvatar && <SkeletonAvatar size="md" />}
        <div className="flex-1 space-y-2">
          <Skeleton height={18} width="60%" rounded="sm" />
          <Skeleton height={14} width="40%" rounded="sm" />
        </div>
      </div>

      {/* Content lines */}
      <SkeletonText lines={lines} />
    </div>
  )
}

/**
 * SkeletonTable - Table placeholder
 */
interface SkeletonTableProps {
  rows?: number
  columns?: number
  showHeader?: boolean
  className?: string
}

export function SkeletonTable({
  rows = 5,
  columns = 4,
  showHeader = true,
  className,
}: SkeletonTableProps) {
  return (
    <div className={cn('w-full', className)}>
      {/* Header */}
      {showHeader && (
        <div className="flex gap-4 pb-4 mb-4 border-b border-light-separator dark:border-dark-separator">
          {Array.from({ length: columns }).map((_, index) => (
            <Skeleton
              key={index}
              height={14}
              width={index === 0 ? '25%' : '18%'}
              rounded="sm"
            />
          ))}
        </div>
      )}

      {/* Rows */}
      <div className="space-y-4">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="flex items-center gap-4">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton
                key={colIndex}
                height={16}
                width={colIndex === 0 ? '25%' : '18%'}
                rounded="sm"
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * SkeletonStatCard - Stat card placeholder
 */
interface SkeletonStatCardProps {
  className?: string
}

export function SkeletonStatCard({ className }: SkeletonStatCardProps) {
  return (
    <div
      className={cn(
        'liquid-glass-card p-6',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <Skeleton width={48} height={48} rounded="xl" />
        <Skeleton width={50} height={20} rounded="sm" />
      </div>

      {/* Value */}
      <Skeleton width="70%" height={32} rounded="sm" className="mb-2" />

      {/* Label */}
      <Skeleton width="50%" height={16} rounded="sm" />
    </div>
  )
}

/**
 * SkeletonList - List items placeholder
 */
interface SkeletonListProps {
  items?: number
  showAvatar?: boolean
  showAction?: boolean
  className?: string
}

export function SkeletonList({
  items = 5,
  showAvatar = true,
  showAction = false,
  className,
}: SkeletonListProps) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: items }).map((_, index) => (
        <div
          key={index}
          className="flex items-center gap-3 p-3 rounded-xl"
        >
          {showAvatar && <SkeletonAvatar size="md" />}
          <div className="flex-1 space-y-2">
            <Skeleton height={16} width="60%" rounded="sm" />
            <Skeleton height={14} width="40%" rounded="sm" />
          </div>
          {showAction && <Skeleton width={80} height={32} rounded="lg" />}
        </div>
      ))}
    </div>
  )
}
